import LoadingIndicator from '@/components/LoadingIndicator'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { useLazyQuery } from '@apollo/client'
import { Close } from '@mui/icons-material'
import { Dialog, IconButton, Stack, useMediaQuery, useTheme } from '@mui/material'

import { useCallback, useMemo, useState } from 'react'

import MenuItemDetail from '../ItemDetail/MenuItemDetail'
import MenuItemDetailV2 from '../ItemDetail/MenuItemDetailV2'
import { CanteenCartItemInput } from '../MenuCart/types'
import { CanteenCartItem } from '../MenuCartV2/types'
import { CartMappingOptionInput, graphqlDataToCartItem } from './cartMapping'
import { canteenMenuItemsMenuItemDataFragment } from './MenuItems'
import { NullifiedContextProvider } from '@dnd-kit/core/dist/components/DragOverlay/components'

const canteenMenuItemModalItemDataQuery = graphql(`
  query CanteenMenuItemModal_ItemDataQuery($vendorId: ID!, $itemId: ID!) {
    vendor(id: $vendorId) {
      ...CanteenMenuItemDetail_VendorDataFragment
      ...CanteenMenuItemDetail_VendorDataFragmentV2
      liveMenuItem(id: $itemId) {
        _id
        ...CanteenMenuItemDetail_ItemDataFragment
        ...CanteenMenuItemDetail_ItemDataFragmentV2
      }
    }
  }
`)

type Props = {
  cartItems: CanteenCartItem[]
  addToCart: (item: CanteenCartItemInput) => void
  hidePrices: boolean
  useNewCcUi: boolean
}

/**
 * Add-to-cart and "see more/options" logic shared by every place a CCMenuItem
 * can be actioned from (the main menu grid, and the order-again strip) — one
 * modal instance and one cart-mapping path, so order-again items go through
 * exactly the same flow as a normal menu item.
 */
export const useMenuItemActions = ({ cartItems, addToCart, hidePrices, useNewCcUi }: Props) => {
  const theme = useTheme()
  const fullScreenItemModal = useMediaQuery(theme.breakpoints.down('sm'))
  // explicitly check for portrait to handle tablet views
  const isPortrait = useMediaQuery('(orientation: portrait)')

  const isAddItemDisabled = useCallback(
    (incomingId: string, orderLimit: number | null, quantityRemaining: number | null) => {
      if (orderLimit === null && quantityRemaining === null) return false

      const allItemsWithId = cartItems.filter((cartItem) => cartItem.id === incomingId)
      const totalOfAllItems = allItemsWithId.reduce((acc, cartItem) => acc + cartItem.qty, 0)

      return totalOfAllItems >= Math.min(orderLimit ?? Infinity, quantityRemaining ?? Infinity)
    },
    [cartItems],
  )

  const [selectedItem, setSelectedItem] = useState<{
    vendorId: string
    itemId: string
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>
    liveVariationQuantityData: {
      orderLimit: number | null
      quantityRemaining: number | null
    }
    ccVariationId: string
  } | null>(null)
  const [getItemData, { data: selectedItemData, loading: loadingItem }] = useLazyQuery(
    canteenMenuItemModalItemDataQuery,
  )

  const selectedItemDataFragment = selectedItem
    ? useFragment(canteenMenuItemsMenuItemDataFragment, selectedItem.itemData)
    : null

  const existingSelectedItemQty = useMemo(() => {
    if (!selectedItem) return 0
    return cartItems
      .filter((cartItem) => cartItem.id === selectedItemDataFragment?.liveMenuItem._id)
      .reduce((acc, cartItem) => acc + cartItem.qty, 0)
  }, [cartItems, selectedItem])

  const maxSelectedItemQuantity = useMemo(() => {
    if (!selectedItem) return null

    const { orderLimit, quantityRemaining } = selectedItem.liveVariationQuantityData

    if (!orderLimit && !quantityRemaining) return null

    const limit = Math.min(orderLimit ?? Infinity, quantityRemaining ?? Infinity)

    return limit - existingSelectedItemQty
  }, [selectedItem, existingSelectedItemQty])

  const isAddSelectedItemDisabled = useMemo(() => {
    if (!selectedItem || !selectedItemDataFragment?.liveMenuItem._id) return false

    return isAddItemDisabled(
      selectedItemDataFragment.liveMenuItem._id,
      selectedItem.liveVariationQuantityData.orderLimit,
      selectedItem.liveVariationQuantityData.quantityRemaining,
    )
  }, [selectedItem, isAddItemDisabled])

  const handleSeeMore = useCallback(
    (
      itemId: string,
      vendorId: string,
      itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
    ) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { quantityRemaining, liveVariation } = useFragment(
        canteenMenuItemsMenuItemDataFragment,
        itemData,
      )
      const liveVariationQuantityData = {
        orderLimit: liveVariation?.orderLimit ?? null,
        quantityRemaining,
      }
      getItemData({ variables: { vendorId, itemId } })
      setSelectedItem({
        vendorId,
        itemId,
        itemData,
        liveVariationQuantityData,
        ccVariationId: liveVariation._id,
      })
    },
    [getItemData],
  )

  const handleAddToCart = useCallback(
    (
      itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
      ccVariationId: string,
      quantity: number,
      options: Array<CartMappingOptionInput>,
      liveVariationQuantityData: {
        orderLimit: number | null
        quantityRemaining: number | null
      } = { orderLimit: null, quantityRemaining: null },
    ) => {
      const { orderLimit, quantityRemaining } = liveVariationQuantityData
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const item = useFragment(canteenMenuItemsMenuItemDataFragment, itemData)

      if (cartItems.findIndex((cartItem) => cartItem.id === item.liveMenuItem._id) >= 0) {
        const existingItemTotals = cartItems
          .filter((cartItem) => cartItem.id === item.liveMenuItem._id)
          .reduce((acc, cartItem) => acc + cartItem.qty, 0)

        if (
          quantity + existingItemTotals >
          Math.min(orderLimit ?? Infinity, quantityRemaining ?? Infinity)
        ) {
          return
        }
      }

      const mappedCartItem = graphqlDataToCartItem(
        item,
        { quantity, ccVariationId },
        options,
        liveVariationQuantityData,
      )
      addToCart(mappedCartItem)
      setSelectedItem(null)
    },
    [cartItems, addToCart],
  )

  const menuItemDetailModal = useNewCcUi ? (
    <Dialog
      maxWidth={'md'}
      sx={{
        '.MuiDialog-paper': {
          height: '80vh',
          ...(!fullScreenItemModal && isPortrait && { maxHeight: '56vh' }),
          width: { md: '75vw', sm: '90vw', xs: '95vw' },
          maxWidth: { md: '75vw', sm: '90vw', xs: '95vw' },
          borderRadius: 5,
        },
      }}
      fullScreen={fullScreenItemModal}
      onClose={() => setSelectedItem(null)}
      open={Boolean(selectedItem?.itemId)}>
      {loadingItem ? <LoadingIndicator /> : null}
      {selectedItemData && selectedItem && !loadingItem ? (
        <MenuItemDetailV2
          isOrderedItem={false}
          maxQuantity={maxSelectedItemQuantity}
          disableAddToCart={isAddSelectedItemDisabled}
          onAddToCart={(quantity, options) => {
            handleAddToCart(
              selectedItem.itemData,
              selectedItem.ccVariationId,
              quantity,
              options,
              selectedItem.liveVariationQuantityData,
            )
          }}
          selectedVariationId={selectedItem.ccVariationId}
          liveVariationQuantityData={selectedItem.liveVariationQuantityData}
          itemData={selectedItemData.vendor.liveMenuItem}
          vendorData={selectedItemData.vendor}
          hidePrices={hidePrices}
          onClose={() => setSelectedItem(null)}
          fullScreen={fullScreenItemModal}
        />
      ) : null}
    </Dialog>
  ) : (
    <Dialog
      sx={{
        '.MuiPaper-root': {
          height: { md: '93vh', xs: '100%' },
          minHeight: { md: '93vh', xs: '100%' },
          overflow: 'scroll',
        },
      }}
      fullScreen={fullScreenItemModal}
      onClose={() => setSelectedItem(null)}
      maxWidth="md"
      fullWidth
      open={Boolean(selectedItem?.itemId)}>
      <Stack direction="row" position="sticky" top={0} right={0} zIndex={5}>
        <IconButton
          onClick={() => setSelectedItem(null)}
          sx={{ zIndex: 5, position: 'absolute', right: 0 }}>
          <Close />
        </IconButton>
      </Stack>
      {loadingItem ? <LoadingIndicator /> : null}
      {selectedItemData && selectedItem && !loadingItem ? (
        <MenuItemDetail
          onAddToCart={(quantity, options) => {
            handleAddToCart(
              selectedItem.itemData,
              selectedItem.ccVariationId,
              quantity,
              options,
              selectedItem.liveVariationQuantityData,
            )
          }}
          selectedVariationId={selectedItem.ccVariationId}
          itemData={selectedItemData.vendor.liveMenuItem}
          vendorData={selectedItemData.vendor}
          hidePrices={hidePrices}
        />
      ) : null}
    </Dialog>
  )

  return { isAddItemDisabled, handleSeeMore, handleAddToCart, menuItemDetailModal }
}
